import type { BlogPost, BlogTopic } from '../../types/blog';
import { BLOG_CATEGORIES } from '../../data/blog/categories';
import { createSlug, formatReadingTime } from '../../utils/blog';
import type { ExchangeRate } from '../../types/currency';

export class AutoBlogGenerator {
  private static instance: AutoBlogGenerator;
  private generatedPosts: BlogPost[] = [];

  static getInstance(): AutoBlogGenerator {
    if (!AutoBlogGenerator.instance) {
      AutoBlogGenerator.instance = new AutoBlogGenerator();
    }
    return AutoBlogGenerator.instance;
  }

  async generateDailyRatePost(rates: ExchangeRate[]): Promise<BlogPost> {
    const today = new Date();
    const dateStr = today.toLocaleDateString('en-NG', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });

    const majorMovements = rates
      .filter(rate => Math.abs(rate.change) > 1)
      .sort((a, b) => Math.abs(b.change) - Math.abs(a.change));

    const content = this.generateDailyContent(rates, majorMovements, dateStr);
    
    return {
      id: `daily-${today.toISOString().split('T')[0]}`,
      title: `Live Naira Exchange Rate Today - ${dateStr}`,
      content,
      excerpt: this.generateExcerpt(content),
      slug: createSlug(`live-naira-exchange-rate-today-${today.toISOString().split('T')[0]}`),
      category: BLOG_CATEGORIES.find(c => c.slug === 'exchange-rates')!,
      tags: ['daily-rates', 'live-rates', 'market-update', 'naira-exchange'],
      author: 'NigeriaFx Market Team',
      publishedAt: today,
      updatedAt: today,
      featuredImage: 'https://images.unsplash.com/photo-1639322537228-f710d846310a',
      readTime: 3,
      isPublished: true,
      isAutoGenerated: true,
      seoTitle: `Live Naira Exchange Rate Today ${dateStr} | Dollar to Naira Black Market Rate`,
      seoDescription: `Get today's live Naira exchange rate: ${rates[0]?.rate.toFixed(2)} NGN/USD. Latest Dollar to Naira black market rate and CBN official exchange rate Nigeria updates.`
    };
  }

  async generateBreakingNewsPost(rate: ExchangeRate, threshold: number): Promise<BlogPost> {
    const content = this.generateBreakingNewsContent(rate, threshold);
    const today = new Date();

    return {
      id: `breaking-${rate.fromCurrency}-${Date.now()}`,
      title: `BREAKING: ${rate.fromCurrency} Rate Hits ₦${rate.rate.toFixed(2)} - ${Math.abs(rate.change).toFixed(1)}% ${rate.change > 0 ? 'Increase' : 'Decrease'}`,
      content,
      excerpt: this.generateExcerpt(content),
      slug: createSlug(`breaking-${rate.fromCurrency}-rate-${rate.rate.toFixed(2)}-${today.toISOString().split('T')[0]}`),
      category: BLOG_CATEGORIES.find(c => c.slug === 'economic-news')!,
      tags: ['breaking-news', 'rate-alert', `${rate.fromCurrency.toLowerCase()}-naira`, 'market-impact'],
      author: 'NigeriaFx Alert System',
      publishedAt: today,
      updatedAt: today,
      featuredImage: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3',
      readTime: 2,
      isPublished: true,
      isAutoGenerated: true,
      seoTitle: `BREAKING: ${rate.fromCurrency} to Naira Rate Hits ₦${rate.rate.toFixed(2)} | Market Impact`,
      seoDescription: `${rate.fromCurrency} to Naira rate ${rate.change > 0 ? 'surges' : 'drops'} ${Math.abs(rate.change).toFixed(1)}% to ₦${rate.rate.toFixed(2)}. Analysis of market impact and trading implications.`
    };
  }

  async generateWeeklyRoundup(weeklyRates: ExchangeRate[][]): Promise<BlogPost> {
    const content = this.generateWeeklyContent(weeklyRates);
    const today = new Date();
    const weekStart = new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000);

    return {
      id: `weekly-${weekStart.toISOString().split('T')[0]}`,
      title: `Weekly Nigerian Forex Market Analysis - Week of ${weekStart.toLocaleDateString('en-NG')}`,
      content,
      excerpt: this.generateExcerpt(content),
      slug: createSlug(`weekly-forex-analysis-${weekStart.toISOString().split('T')[0]}`),
      category: BLOG_CATEGORIES.find(c => c.slug === 'market-analysis')!,
      tags: ['weekly-analysis', 'market-trends', 'forex-roundup', 'nigerian-forex'],
      author: 'NigeriaFx Analytics Team',
      publishedAt: today,
      updatedAt: today,
      featuredImage: 'https://images.unsplash.com/photo-1642790106117-e829e14a795f',
      readTime: 5,
      isPublished: true,
      isAutoGenerated: true,
      seoTitle: `Weekly Nigerian Forex Market Analysis | Dollar to Naira Trends & Predictions`,
      seoDescription: `Weekly analysis of Nigerian forex market rates, Dollar to Naira black market trends, and Euro to Naira exchange rate predictions for the coming week.`
    };
  }

  private generateDailyContent(rates: ExchangeRate[], majorMovements: ExchangeRate[], dateStr: string): string {
    const topRate = rates[0];
    return `# Live Naira Exchange Rate Today - ${dateStr}

## Today's Market Overview

The Nigerian forex market opened with mixed movements across major currency pairs. The Dollar to Naira black market rate currently stands at **₦${topRate.rate.toFixed(2)}**, showing a ${topRate.change >= 0 ? 'gain' : 'decline'} of **${Math.abs(topRate.change).toFixed(2)}%** from yesterday's close.

## Major Currency Movements

${majorMovements.length > 0 ? majorMovements.map(rate => 
  `### ${rate.fromCurrency}/NGN: ₦${rate.rate.toFixed(2)} (${rate.change >= 0 ? '+' : ''}${rate.change.toFixed(2)}%)
  
The ${rate.fromCurrency} to Naira rate ${rate.change > 0 ? 'strengthened' : 'weakened'} significantly today, reflecting ${rate.change > 0 ? 'increased demand' : 'reduced pressure'} in the parallel market.`
).join('\n\n') : 'No significant movements recorded today across major currency pairs.'}

## Current Exchange Rates Summary

| Currency | Rate (NGN) | Change (%) | Market Status |
|----------|------------|------------|---------------|
${rates.map(rate => 
  `| ${rate.fromCurrency} | ₦${rate.rate.toFixed(2)} | ${rate.change >= 0 ? '+' : ''}${rate.change.toFixed(2)}% | ${Math.abs(rate.change) > 1 ? 'Volatile' : 'Stable'} |`
).join('\n')}

## Market Factors

Today's rate movements can be attributed to several key factors:

- **Supply and demand dynamics** in the parallel market
- **Official policy** and intervention measures
- **Global economic conditions** affecting foreign currency inflows
- **Local economic indicators** and market sentiment

## Trading Recommendations

For traders monitoring the live Naira exchange rate today:

1. **Monitor volatility** - Rates showing high volatility may present opportunities
2. **Check multiple sources** - Always verify rates across different platforms
3. **Consider timing** - Market conditions can change rapidly throughout the day
4. **Risk management** - Never trade more than you can afford to lose

*This analysis is for informational purposes only and should not be considered financial advice.*`;
  }

  private generateBreakingNewsContent(rate: ExchangeRate, threshold: number): string {
    const direction = rate.change > 0 ? 'surge' : 'decline';
    const impact = Math.abs(rate.change) > 3 ? 'significant' : 'notable';
    
    return `# BREAKING: ${rate.fromCurrency} Rate Hits ₦${rate.rate.toFixed(2)} - ${Math.abs(rate.change).toFixed(1)}% ${direction.toUpperCase()}

## Rate Change Alert

The ${rate.fromCurrency} to Naira exchange rate has experienced a ${impact} ${direction} of **${Math.abs(rate.change).toFixed(2)}%**, reaching **₦${rate.rate.toFixed(2)}** in today's trading session.

## Immediate Market Impact

This ${direction} represents a ${impact} movement that exceeds our ${threshold}% alert threshold, indicating:

- **${rate.change > 0 ? 'Increased demand' : 'Reduced pressure'}** for ${rate.fromCurrency} in the parallel market
- **Market volatility** may continue in the short term
- **Trading opportunities** for experienced forex traders

## What This Means for Traders

### For ${rate.fromCurrency} Holders:
${rate.change > 0 ? 
  '- Consider taking profits if you bought at lower rates\n- Monitor for potential reversal signals\n- Be cautious of further volatility' :
  '- Potential buying opportunity for long-term holders\n- Wait for stabilization before major positions\n- Consider dollar-cost averaging approach'
}

*This is a market alert for informational purposes only. Not financial advice.*`;
  }

  private generateWeeklyContent(weeklyRates: ExchangeRate[][]): string {
    const startOfWeek = weeklyRates[0];
    const endOfWeek = weeklyRates[weeklyRates.length - 1];
    
    return `# Weekly Nigerian Forex Market Analysis

## Week in Review

This week showed ${this.getWeeklyTrend(startOfWeek, endOfWeek)} across major currency pairs, with the Dollar to Naira black market rate ${this.getWeeklyPerformance(startOfWeek[0], endOfWeek[0])}.

## Key Movements

${this.generateWeeklyMovements(startOfWeek, endOfWeek)}

## Market Drivers

Several factors influenced this week's performance:

- **Economic indicators** released during the week
- **Global market sentiment** and risk appetite
- **CBN policy signals** and intervention activities
- **Local demand patterns** and seasonal factors

## Looking Ahead

Next week's outlook suggests:
- Continued monitoring of global economic developments
- Potential impact from upcoming economic data releases
- Market positioning ahead of month-end flows

*Weekly analysis is based on market data and should be used for informational purposes only.*`;
  }

  private getWeeklyTrend(start: ExchangeRate[], end: ExchangeRate[]): string {
    const avgStartRate = start.reduce((sum, rate) => sum + rate.rate, 0) / start.length;
    const avgEndRate = end.reduce((sum, rate) => sum + rate.rate, 0) / end.length;
    const weeklyChange = ((avgEndRate - avgStartRate) / avgStartRate) * 100;
    
    return weeklyChange > 0 ? 'upward momentum' : 'downward pressure';
  }

  private getWeeklyPerformance(startRate: ExchangeRate, endRate: ExchangeRate): string {
    const change = ((endRate.rate - startRate.rate) / startRate.rate) * 100;
    return `${change > 0 ? 'gaining' : 'losing'} ${Math.abs(change).toFixed(2)}% for the week`;
  }

  private generateWeeklyMovements(start: ExchangeRate[], end: ExchangeRate[]): string {
    return start.map((startRate, index) => {
      const endRate = end[index];
      const weeklyChange = ((endRate.rate - startRate.rate) / startRate.rate) * 100;
      
      return `### ${startRate.fromCurrency}/NGN
- **Start of week**: ₦${startRate.rate.toFixed(2)}
- **End of week**: ₦${endRate.rate.toFixed(2)}
- **Weekly change**: ${weeklyChange >= 0 ? '+' : ''}${weeklyChange.toFixed(2)}%`;
    }).join('\n\n');
  }

  private generateExcerpt(content: string): string {
    const firstParagraph = content.split('\n\n')[1] || content.split('\n')[0];
    return firstParagraph.replace(/[#*]/g, '').substring(0, 160) + '...';
  }

  getGeneratedPosts(): BlogPost[] {
    return this.generatedPosts;
  }

  addGeneratedPost(post: BlogPost): void {
    this.generatedPosts.unshift(post);
    // Keep only last 50 auto-generated posts
    if (this.generatedPosts.length > 50) {
      this.generatedPosts = this.generatedPosts.slice(0, 50);
    }
  }
}